import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

export const Field = ({ label, value, onChange, type = "text", rows }) => (
  <label className="block mb-4">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#6E6151]">{label}</span>
    {rows ? (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="mt-1 w-full bg-[#DDD3C1] border border-[#2B2118]/10 p-3 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118]" />
    ) : (
      <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="mt-1 w-full bg-[#DDD3C1] border border-[#2B2118]/10 p-3 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118]" />
    )}
  </label>
);

export const ImageField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(`${API}/files/${data.path}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="mb-1">
      <Field label={`${label} (URL)`} value={value} onChange={onChange} />
      <div className="flex items-center gap-3 -mt-2 mb-4">
        <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="image-upload-input" />
        <button type="button" data-testid="image-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy}
          className="border border-[#8F6B3A]/40 text-[#8F6B3A] px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors disabled:opacity-50">
          {busy ? "Uploading…" : "Upload image"}
        </button>
        {value && <img src={value} alt="" className="w-10 h-10 object-cover border border-[#2B2118]/10" />}
      </div>
    </div>
  );
};

export const SaveBtn = ({ onClick, testid, children = "Save" }) => (
  <button data-testid={testid} onClick={onClick} className="bg-[#8F6B3A] text-[#FBF8F1] px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors">
    {children}
  </button>
);

export const GalleryField = ({ value = [], onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        urls.push(`${API}/files/${data.path}`);
      }
      onChange([...(value || []), ...urls]);
      toast.success(`${urls.length} image(s) added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="mb-4">
      <span className="text-[10px] tracking-[0.2em] uppercase text-[#6E6151]">Gallery images</span>
      <div className="flex flex-wrap gap-3 mt-2">
        {(value || []).map((url, i) => (
          <div key={i} className="relative group">
            <img src={url} alt="" className="w-20 h-20 object-cover border border-[#2B2118]/10" />
            <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500/90 text-white text-xs leading-none opacity-0 group-hover:opacity-100 transition-opacity">×</button>
          </div>
        ))}
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="gallery-upload-input" />
        <button type="button" data-testid="gallery-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-20 h-20 border border-dashed border-[#8F6B3A]/40 text-[#8F6B3A] text-[10px] tracking-[0.1em] uppercase hover:bg-[#8F6B3A]/10 transition-colors disabled:opacity-50">
          {busy ? "…" : "+ Add"}
        </button>
      </div>
    </div>
  );
};

const empty = { name: "", slug: "", gender: "Women", price: 0, tagline: "", description: "", hex: "#8F6B3A", image: "", longevity: 3, sillage: 3, occasion: "", season: "", notes: { top: [], heart: [], base: [] }, gallery: [], related: [], active: true };

export const AdminFragrances = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const load = () => api.get("/admin/fragrances").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...edit, notes: { top: strToList(edit._top), heart: strToList(edit._heart), base: strToList(edit._base) }, related: strToList(edit._related), gallery: edit.gallery || [] };
    delete body._top; delete body._heart; delete body._base; delete body._related; delete body.id; delete body.order;
    try {
      if (isNew) await api.post("/admin/fragrances", body);
      else await api.put(`/admin/fragrances/${edit.slug}`, body);
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };
  const strToList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const listToStr = (l) => (l || []).join(", ");
  const openEdit = (f, isNewF) => {
    setIsNew(isNewF);
    setEdit({ ...f, _top: listToStr(f.notes?.top), _heart: listToStr(f.notes?.heart), _base: listToStr(f.notes?.base), _related: listToStr(f.related) });
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this fragrance?")) return;
    await api.delete(`/admin/fragrances/${slug}`);
    toast.success("Deleted"); load();
  };

  if (edit) return (
    <div>
      <div className="grid md:grid-cols-2 gap-x-6">
        <Field label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
        <Field label="Slug" value={edit.slug} onChange={(v) => setEdit({ ...edit, slug: v })} />
        <label className="block mb-4">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#6E6151]">Gender</span>
          <select value={edit.gender} onChange={(e) => setEdit({ ...edit, gender: e.target.value })} data-testid="fragrance-gender-select"
            className="mt-1 w-full bg-[#DDD3C1] border border-[#2B2118]/10 p-3 text-sm text-[#2B2118]">
            {["Women", "Men", "Unisex"].map((g) => <option key={g}>{g}</option>)}
          </select>
        </label>
        <Field label="Price (PKR)" type="number" value={edit.price} onChange={(v) => setEdit({ ...edit, price: v })} />
        <Field label="Hex accent" value={edit.hex} onChange={(v) => setEdit({ ...edit, hex: v })} />
        <ImageField label="Image" value={edit.image} onChange={(v) => setEdit({ ...edit, image: v })} />
        <Field label="Tagline" value={edit.tagline} onChange={(v) => setEdit({ ...edit, tagline: v })} />
        <Field label="Occasion" value={edit.occasion} onChange={(v) => setEdit({ ...edit, occasion: v })} />
        <Field label="Season" value={edit.season} onChange={(v) => setEdit({ ...edit, season: v })} />
        <Field label="Longevity (1-5)" type="number" value={edit.longevity} onChange={(v) => setEdit({ ...edit, longevity: v })} />
        <Field label="Sillage (1-5)" type="number" value={edit.sillage} onChange={(v) => setEdit({ ...edit, sillage: v })} />
        <Field label="Related slugs (comma separated)" value={edit._related} onChange={(v) => setEdit({ ...edit, _related: v })} />
      </div>
      <Field label="Description" rows={3} value={edit.description} onChange={(v) => setEdit({ ...edit, description: v })} />
      <Field label="Top notes (comma separated)" value={edit._top} onChange={(v) => setEdit({ ...edit, _top: v })} />
      <Field label="Heart notes" value={edit._heart} onChange={(v) => setEdit({ ...edit, _heart: v })} />
      <Field label="Base notes" value={edit._base} onChange={(v) => setEdit({ ...edit, _base: v })} />
      <GalleryField value={edit.gallery} onChange={(v) => setEdit({ ...edit, gallery: v })} />
      <label className="flex items-center gap-2 mb-2 text-sm text-[#5A4C3C]">
        <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} data-testid="fragrance-active-checkbox" /> Active (visible on site)
      </label>
      <label className="flex items-center gap-2 mb-6 text-sm text-[#5A4C3C]">
        <input type="checkbox" checked={edit.sold_out || false} onChange={(e) => setEdit({ ...edit, sold_out: e.target.checked })} data-testid="fragrance-soldout-checkbox" /> Sold out (page shows "back soon" instead of Add to Cart)
      </label>
      <div className="flex gap-3">
        <SaveBtn onClick={save} testid="fragrance-save-btn" />
        <button data-testid="fragrance-cancel-btn" onClick={() => setEdit(null)} className="border border-[#2B2118]/20 px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-[#5A4C3C] hover:text-[#2B2118]">Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">Fragrances</h2>
        <SaveBtn onClick={() => openEdit(empty, true)} testid="fragrance-add-btn">Add new</SaveBtn>
      </div>
      <div className="divide-y divide-[#2B2118]/10 border border-[#2B2118]/10">
        {list.map((f) => (
          <div key={f.slug} className="flex items-center gap-4 p-4" data-testid={`admin-fragrance-row-${f.slug}`}>
            <img src={f.image} alt="" className="w-16 h-12 object-cover" />
            <div className="flex-1">
              <p className="text-sm">{f.name} {!f.active && <span className="text-[10px] text-red-400 ml-2">HIDDEN</span>}{f.sold_out && <span className="text-[10px] text-amber-400 ml-2">SOLD OUT</span>}</p>
              <p className="text-xs text-[#6E6151]">{f.gender} · PKR {f.price.toLocaleString()}</p>
            </div>
            <button data-testid={`fragrance-edit-${f.slug}`} onClick={() => openEdit(f, false)} className="text-xs text-[#8F6B3A] hover:underline">Edit</button>
            <button data-testid={`fragrance-delete-${f.slug}`} onClick={() => remove(f.slug)} className="text-xs text-red-400/70 hover:text-red-400">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminDecants = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/decants").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const save = async () => {
    const body = { ...d, slugs: typeof d.slugs === "string" ? d.slugs.split(",").map((x) => x.trim()) : d.slugs };
    await api.put("/admin/decants", { data: body });
    toast.success("Saved");
  };
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-6">Decants — Discovery Set</h2>
      <Field label="Title" value={d.title} onChange={(v) => setD({ ...d, title: v })} />
      <Field label="Price — 3-piece set (PKR)" type="number" value={d.price} onChange={(v) => setD({ ...d, price: v })} />
      <Field label="Price — single decant (PKR)" type="number" value={d.single_price} onChange={(v) => setD({ ...d, single_price: v })} />
      <ImageField label="Decants image" value={d.image} onChange={(v) => setD({ ...d, image: v })} />
      <Field label="Size label" value={d.size} onChange={(v) => setD({ ...d, size: v })} />
      <Field label="Slugs (comma separated)" value={Array.isArray(d.slugs) ? d.slugs.join(", ") : d.slugs} onChange={(v) => setD({ ...d, slugs: v })} />
      <Field label="Intro" rows={3} value={d.intro} onChange={(v) => setD({ ...d, intro: v })} />
      <Field label="What is a decant?" rows={5} value={d.what_is} onChange={(v) => setD({ ...d, what_is: v })} />
      <SaveBtn onClick={save} testid="decants-save-btn" />
    </div>
  );
};
